import { useTheme } from '@stores/useTheme';
import { useContext, useRef } from 'react';
import CustomScrollBar from '@components/common/CustomScrollBar';
import { useLanguage } from '@utils/hooks/useLanguage';
import H2H from '@components/StatsConsole/Widgets/H2HWidget/parts/H2H/H2H';
import Table from '@components/StatsConsole/Widgets/H2HWidget/parts/Table/Table';
import GroupTable from '@components/StatsConsole/Widgets/H2HWidget/parts/GroupTable/GroupTable';
import Knockout from '@components/StatsConsole/Widgets/H2HWidget/parts/Knockout/Knockout';
import FiltersH2H from '@components/StatsConsole/Widgets/H2HWidget/parts/FiltersH2H';
import { StateContext } from '../../../../state/stateContext';
import { T } from '@utils/enums';
import { isDataAvailable } from '@utils/functions';
import Spinner from '@components/common/Spinner';
import RefreshIconAndText from '@components/common/RefreshIconAndText';
import { useLoadingWithRefresh } from '@utils/hooks/useLoadingWithRefresh';
import { useTranslations } from '../../../../translation/TranslationContext';

interface IPlayersWidgetProps {
    eventId: string | null;
    h2hData: any;
}

const H2HWidget = ({ eventId, h2hData }: IPlayersWidgetProps) => {
    const { theme } = useTheme();
    const language = useLanguage();
    const translations = useTranslations(language);

    const ctx = useContext(StateContext);
    const { stContext, setStContext } = ctx || {};
    const { h2hTabActiveFilter, h2hTabActiveFilterName } = stContext || {};
    const { isLoading, showRefreshIcon } = useLoadingWithRefresh({
        data: isDataAvailable(h2hData?.data) ? h2hData?.data : null, 
        error: h2hData?.error
    }, 5000);

    const menuItemsH2HContainerRef = useRef<HTMLDivElement>(null);

    const getH2HData = () => {
        if (Array.isArray(h2hData?.data?.match_statistics)) {
            const h2hItem = h2hData.data.match_statistics.find((item: any) => item.type === 'h2h');
            return h2hItem?.data;
        }
        return null;
    };


    const getTableData = () => {
        if (Array.isArray(h2hData?.data?.match_statistics)) {
            const tableItem = h2hData.data.match_statistics.find((item: any) => item.type === 'table');
            return tableItem?.data;
        }
        return null;
    };

    const getSpecificKnockoutData = (label: string) => {
        if (Array.isArray(h2hData?.data?.match_statistics)) {
            const knockoutItem = h2hData.data.match_statistics.find((item: any) => 
                item.type === 'knockout' && item.label === label
            );
            return knockoutItem?.data;
        }
        return null;
    };

    const getGroupsTablesData = () => {
        if (Array.isArray(h2hData?.data?.match_statistics)) {
            const groupsItem = h2hData.data.match_statistics.find((item: any) => item.type === 'groups_tables');
            return groupsItem?.data;
        }
        return null;
    };

    const getQualificationData = () => {
        if (Array.isArray(h2hData?.data?.match_statistics)) {
            const qualificationItem = h2hData.data.match_statistics.find((item: any) => 
                item.type === 'qualification' || 
                (item.type === 'knockout' && item.label === 'InHouseStats.QualifyingRound')
            );
            return qualificationItem?.data;
        }
        return null;
    };

    if (h2hData?.error || !isDataAvailable(h2hData?.data)) {
        return showRefreshIcon ? (
            <RefreshIconAndText addClass="pt-[100px]" />
        ) : (
            <Spinner />
        );
    }

    const renderActiveView = () => {
        switch (h2hTabActiveFilter as string) {
            case 'H2H':
                const h2hData = getH2HData();
                if (!h2hData) {
                    return showRefreshIcon ? (
                        <RefreshIconAndText addClass="pt-[100px]" />
                    ) : (
                        <Spinner />
                    );
                }

                return <H2H h2hData={h2hData} />;
            case 'TABLE':
                const tableData = getTableData();
                if (!tableData) {
                    return (
                        <RefreshIconAndText addClass="pt-[100px]" />
                    );
                }
                return <Table tableData={tableData} colorLegend={tableData?.colorLegend} />;
            case 'QUARTER_FINALS':
                const quarterFinalsData = getSpecificKnockoutData('InHouseStats.QuarterFinals');
                if (!quarterFinalsData) {
                    return (
                        <RefreshIconAndText addClass="pt-[100px]" />
                    );
                }
                return <Knockout knockoutData={quarterFinalsData} />;
            case 'ROUND_OF_16':
                const roundOf16Data = getSpecificKnockoutData('InHouseStats.RoundOf16');
                if (!roundOf16Data) {
                    return (
                        <RefreshIconAndText addClass="pt-[100px]" />
                    );
                }
                return <Knockout knockoutData={roundOf16Data} />;
            case 'KNOCKOUT':
                const knockoutData = getSpecificKnockoutData('InHouseStats.Playoffs');
                if (!knockoutData) {
                    return <RefreshIconAndText addClass="pt-[100px]" />;
                }
                return <Knockout knockoutData={knockoutData} />;
            case 'QUALIFICATION':
                const qualificationData = getQualificationData();
                if (!qualificationData) {
                    return (
                        <RefreshIconAndText addClass="pt-[100px]" />
                    );
                }
                return <Knockout knockoutData={qualificationData} />;
            
            case 'GROUPS_TABLES':
                const groupsData = getGroupsTablesData();
                if (!groupsData) {
                    return <RefreshIconAndText addClass="pt-[100px]" />
                }
                return <GroupTable groupsData={groupsData} />;
            default:
                return <H2H h2hData={getH2HData()} />;
        }
    };

    return (
        <div data-testid="h2h-widget" className={`${theme === T.DARK ? 'tinny-scrollbar-dark' : 'tinny-scrollbar'} 
                        flex flex-col w-full overflow-y-hidden overflow-x-hidden`}>
            <CustomScrollBar viewId={T.TAB_H2H} paddingTopOff={true} height='h-auto'>
                <div className='w-full min-h-[5px] h-[5px] bg-[#F5F5F5]'></div>
                <div ref={menuItemsH2HContainerRef} className='w-[100%] flex justify-center items-center mt-[8px]'>
                    <FiltersH2H 
                        h2hData={h2hData} 
                        menuItemsH2HContainerRef={menuItemsH2HContainerRef}
                    />
                </div>
                <div data-testid="h2h-content" className='flex flex-col justify-center items-center px-[16px]'>
                    {renderActiveView()}
                </div>
            </CustomScrollBar>
        </div>
    )
}

export default H2HWidget;
